/**
 * Model Registry - v13 Section 6.10-6.11
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  NO HARDCODED MODEL DATA - All model info comes from provider APIs           ║
 * ║                                                                              ║
 * ║  This file provides functions to GET metadata from ConfigService.            ║
 * ║  We do NOT maintain a registry of model names, context windows, or pricing. ║
 * ║  We do NOT use pattern matching to infer model properties.                   ║
 * ║                                                                              ║
 * ║  When metadata is not available, functions return null values.               ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */
import { LLMProviderType } from './base';
import { configService } from '../config';
// ============================================================================
// METADATA FUNCTIONS - No hardcoded values, use ConfigService
// ============================================================================
/**
 * Get model metadata - returns null values when not available
 *
 * NOTE: For accurate data, use getModelMetadataAsync() which fetches from ConfigService.
 * This sync version exists for backward compatibility but returns nulls for most fields.
 */
export function getModelMetadata(_modelName) {
    // No pattern matching - return null for all data fields
    // Provider is unknown without API data
    return {
        provider: LLMProviderType.OPENAI, // Default fallback - actual provider from API
        contextWindow: null,
        maxCompletionTokens: null,
        inputPricePer1k: null,
        outputPricePer1k: null,
        tier: null,
        zeroDataRetention: null,
        isReasoningModel: null,
    };
}
/**
 * Get context window for a model
 * Returns null when not available - callers should handle null
 */
export function getContextWindow(_modelName, _logger) {
    // No defaults - data should come from ConfigService
    return null;
}
/**
 * Get max completion tokens for a model
 * Returns null when not available - callers should handle null
 */
export function getMaxCompletionTokens(_modelName, _logger) {
    // No defaults - data should come from ConfigService
    return null;
}
/**
 * Calculate cost from token usage
 *
 * NOTE: Returns 0 when pricing unavailable. Use calculateCostAsync for accurate pricing.
 */
export function calculateCost(_modelName, _inputTokens, _outputTokens) {
    // No default pricing - return 0 and log warning
    // Callers should use calculateCostAsync for accurate pricing
    return 0;
}
/**
 * Get model pricing
 *
 * NOTE: Returns null when unavailable. Use getModelPricingAsync for accurate pricing.
 */
export function getModelPricing(_modelName) {
    try {
        const pricing = configService.getPricingSync(_modelName);
        if (!pricing)
            return null;
        return {
            inputPer1k: pricing.inputPer1M / 1000,
            outputPer1k: pricing.outputPer1M / 1000,
        };
    }
    catch {
        return null;
    }
}
/**
 * Check if a model supports zero data retention
 *
 * NOTE: Returns null when unknown. For accurate info, use ConfigService.
 */
export function isZDRModel(_modelName) {
    // No pattern matching - data should come from ConfigService
    return null;
}
/**
 * Check if a model is a reasoning model
 *
 * NOTE: Returns null when unknown. For accurate info, use ConfigService.
 */
export function isReasoningModel(_modelName) {
    // No pattern matching - data should come from ConfigService
    return null;
}
// ============================================================================
// ASYNC FUNCTIONS (Use ConfigService - the proper way to get model data)
// ============================================================================
/**
 * Map config provider string to LLMProviderType enum
 */
function mapProviderToEnum(provider) {
    const providerMap = {
        'openai': LLMProviderType.OPENAI,
        'anthropic': LLMProviderType.ANTHROPIC,
        'google': LLMProviderType.GOOGLE,
        'groq': LLMProviderType.GROQ,
        'deepinfra': LLMProviderType.DEEPINFRA,
        'ollama': LLMProviderType.OLLAMA,
    };
    return providerMap[provider] ?? LLMProviderType.OPENAI;
}
/**
 * Get model metadata (async, uses ConfigService)
 * This is the preferred way to get model metadata - no pattern matching.
 */
export async function getModelMetadataAsync(modelName) {
    try {
        const metadata = await configService.getModelMetadata(modelName);
        if (metadata) {
            return {
                provider: mapProviderToEnum(metadata.provider),
                contextWindow: metadata.contextWindow,
                maxCompletionTokens: metadata.maxCompletionTokens,
                inputPricePer1k: metadata.pricing ? metadata.pricing.inputPer1M / 1000 : null,
                outputPricePer1k: metadata.pricing ? metadata.pricing.outputPer1M / 1000 : null,
                tier: null, // Tiers are application-level config, not model metadata
                zeroDataRetention: metadata.zeroDataRetention,
                isReasoningModel: metadata.isReasoningModel,
            };
        }
    }
    catch {
        // ConfigService not available
    }
    return null;
}
/**
 * Calculate cost with ConfigService pricing (async)
 */
export async function calculateCostAsync(modelName, inputTokens, outputTokens) {
    try {
        const pricing = await configService.getPricing(modelName);
        return (inputTokens * pricing.inputPer1M + outputTokens * pricing.outputPer1M) / 1000000;
    }
    catch {
        return 0; // No default pricing - return 0 when unavailable
    }
}
/**
 * Get model pricing (async, uses ConfigService)
 */
export async function getModelPricingAsync(modelName) {
    try {
        const pricing = await configService.getPricing(modelName);
        return {
            inputPer1k: pricing.inputPer1M / 1000,
            outputPer1k: pricing.outputPer1M / 1000,
        };
    }
    catch {
        return null; // No default pricing
    }
}
// ============================================================================
// DEPRECATED - These existed for backward compatibility
// ============================================================================
/**
 * @deprecated No longer maintains a model registry
 */
export const MODEL_REGISTRY = {};
/**
 * @deprecated Use configService.getModelsByProvider()
 */
export function getModelsForProvider(_provider) {
    return [];
}
/**
 * @deprecated Tiers should be configured at application level, not inferred
 */
export function getModelsForTier(_tier) {
    return [];
}
/**
 * @deprecated Use ConfigService to check zeroDataRetention property
 */
export function getZDRModels() {
    return [];
}
/**
 * @deprecated Models should be fetched from provider APIs
 */
export function getRecommendedModel(_tier, _preferZDR = false, _preferredProvider) {
    return null;
}
/**
 * @deprecated Use configService.getModelsByProvider()
 */
export async function getModelsForProviderAsync(provider) {
    try {
        return await configService.getModelsByProvider(provider);
    }
    catch {
        return [];
    }
}
/**
 * @deprecated Tiers should be configured at application level, not hardcoded
 */
export const MODEL_TIERS = {
    fast: { models: [], avgCostPer1k: 0, description: 'Configure at application level' },
    standard: { models: [], avgCostPer1k: 0, description: 'Configure at application level' },
    quality: { models: [], avgCostPer1k: 0, description: 'Configure at application level' },
    local: { models: [], avgCostPer1k: 0, description: 'Configure at application level' },
};
